import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Input,
  Select,
  Spinner,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ReactNode, useMemo, useState } from 'react';
import { FiEdit2, FiInbox, FiSearch, FiTrash2, FiUsers } from 'react-icons/fi';
import { UseQueryResult } from 'react-query';

import {
  AccessControlItem,
  AccessControlKind,
} from '../../../../services/accessControlService';
import {
  ItemFilter,
  KIND_LABEL,
  filterItems,
  itemIssues,
  plural,
} from '../../../../utils/accessControl';

interface Props {
  kind: AccessControlKind;
  query: UseQueryResult<AccessControlItem[]>;
  onUsers: (item: AccessControlItem) => void;
  onEdit: (item: AccessControlItem) => void;
  onDelete: (item: AccessControlItem) => void;
}

function Label({ children }: { children: ReactNode }) {
  return (
    <Text fontWeight="medium" color="gray.650" mr="10px" whiteSpace="nowrap">
      {children}
    </Text>
  );
}

const ISSUE_TAGS = {
  duplicate: {
    color: 'red',
    text: 'Código repetido',
    tip: 'Outro item tem a mesma identificação. Para o sistema os dois são iguais; mantenha só um.',
  },
  format: {
    color: 'orange',
    text: 'Código fora do padrão',
    tip: 'Tem espaço, maiúscula ou acento. Continua funcionando, mas é fácil digitar diferente no código.',
  },
  'no-users': {
    color: 'gray',
    text: 'Sem usuários',
    tip: 'Ninguém tem este item.',
  },
};

export function AccessItemsTab({
  kind,
  query,
  onUsers,
  onEdit,
  onDelete,
}: Props) {
  const label = KIND_LABEL[kind];
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ItemFilter>('all');

  const items = useMemo(() => query.data || [], [query.data]);
  const filtered = filterItems(items, search, filter);
  const withIssues = items.filter(
    item => itemIssues(item, items).filter(i => i !== 'no-users').length > 0,
  ).length;
  const withoutUsers = items.filter(item => item.total_accesses === 0).length;

  return (
    <Box>
      {kind === 'role' && (
        <Alert status="info" color="blue.900" borderRadius="md" mb="4">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Funções agrupam pessoas pelo papel (ex.: Gestor de processos). O que
            libera telas e relatórios no SAMASC são as permissões.
          </AlertDescription>
        </Alert>
      )}

      <Flex
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gridGap="3"
        mb="4"
      >
        <Flex gridGap="2" direction={{ base: 'column', sm: 'row' }}>
          <Box position="relative" w={{ base: '100%', sm: '300px' }}>
            <Icon
              as={FiSearch}
              position="absolute"
              left="3"
              top="50%"
              transform="translateY(-50%)"
              color="gray.400"
              zIndex={1}
            />
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar por nome ou identificação"
              aria-label={`Buscar ${label.one}`}
              pl="10"
            />
          </Box>
          <Select
            value={filter}
            onChange={event => setFilter(event.target.value as ItemFilter)}
            aria-label="Filtrar"
            w={{ base: '100%', sm: '230px' }}
          >
            <option value="all">Todas</option>
            <option value="with-users">Com usuários</option>
            <option value="no-users">Sem usuários</option>
            <option value="issues">Com pontos de atenção</option>
          </Select>
        </Flex>

        {query.data && (
          <Text fontSize="sm" color="gray.500">
            {plural(items.length, label.one, label.many)} ·{' '}
            {plural(withoutUsers, 'sem usuários', 'sem usuários')} ·{' '}
            {plural(withIssues, 'com atenção', 'com atenção')}
          </Text>
        )}
      </Flex>

      {query.isLoading && (
        <Flex minH="200px" align="center" justify="center">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      )}

      {query.isError && !query.data && (
        <Alert status="error" color="red.900" borderRadius="md">
          <AlertIcon />
          <AlertDescription flex="1">
            Não foi possível carregar as {label.many}.
          </AlertDescription>
          <Button size="sm" onClick={() => query.refetch()}>
            Tentar novamente
          </Button>
        </Alert>
      )}

      {query.data && filtered.length === 0 && (
        <Center py="10" flexDirection="column">
          <Icon as={FiInbox} fontSize="40" color="gray.500" />
          <Text color="gray.400" mt="3">
            Nenhuma {label.one} encontrada
          </Text>
        </Center>
      )}

      {filtered.map(item => {
        const issues = itemIssues(item, items);
        const inactive = item.total_accesses - item.active_accesses;

        return (
          <Box
            key={item.id}
            bg="gray.800"
            p="10px"
            borderRadius="6px"
            mb="10px"
          >
            <Flex justify="space-between" align="flex-start" gridGap="3">
              <Flex align="center" gridGap="2" wrap="wrap" minW="0">
                <Text as="h2">{item.name}</Text>
                {issues.map(issue => (
                  <Tooltip
                    key={issue}
                    bg="gray.650"
                    label={ISSUE_TAGS[issue].tip}
                  >
                    <Tag size="sm" colorScheme={ISSUE_TAGS[issue].color}>
                      {ISSUE_TAGS[issue].text}
                    </Tag>
                  </Tooltip>
                ))}
              </Flex>

              <HStack spacing="2" flexShrink={0}>
                <Tooltip bg="gray.650" label="Ver e alterar os usuários">
                  <Button
                    onClick={() => onUsers(item)}
                    colorScheme="blue"
                    color="white"
                    leftIcon={<FiUsers />}
                    aria-label={`Usuários de ${item.name}`}
                  >
                    {item.total_accesses}
                  </Button>
                </Tooltip>
                <Tooltip bg="gray.650" label="Editar">
                  <Button
                    onClick={() => onEdit(item)}
                    colorScheme="green"
                    color="white"
                    aria-label={`Editar ${item.name}`}
                  >
                    <FiEdit2 />
                  </Button>
                </Tooltip>
                <Tooltip bg="gray.650" label="Excluir">
                  <Button
                    onClick={() => onDelete(item)}
                    colorScheme="red"
                    color="white"
                    aria-label={`Excluir ${item.name}`}
                  >
                    <FiTrash2 />
                  </Button>
                </Tooltip>
              </HStack>
            </Flex>

            <Flex
              mt="1"
              direction={{ base: 'column', lg: 'row' }}
              gridGap={{ base: '2', lg: '0' }}
              align={{ base: 'stretch', lg: 'flex-start' }}
            >
              <Box minW={{ lg: '420px' }} maxW={{ lg: '520px' }}>
                <Flex align="baseline" wrap="wrap">
                  <Label>{label.title}:</Label>
                  <Text>{item.name}</Text>
                </Flex>
                <Flex align="baseline" wrap="wrap">
                  <Label>Identificação:</Label>
                  <Text fontFamily="mono" fontSize="sm" wordBreak="break-all">
                    {item.identification || '—'}
                  </Text>
                </Flex>
                <Flex align="center" wrap="wrap" gridGap="1" mt="1">
                  <Label>Usuários:</Label>
                  <Tag
                    size="sm"
                    colorScheme={item.active_accesses ? 'green' : 'gray'}
                  >
                    {plural(item.active_accesses, 'ativo', 'ativos')}
                  </Tag>
                  {inactive > 0 && (
                    <Tag size="sm" colorScheme="gray">
                      {plural(inactive, 'inativo', 'inativos')}
                    </Tag>
                  )}
                  {item.bi_reports > 0 && (
                    <Tooltip
                      bg="gray.650"
                      label="Relatórios do Business Intelligence liberados por este item"
                    >
                      <Tag size="sm" colorScheme="purple">
                        {plural(
                          item.bi_reports,
                          'relatório de BI',
                          'relatórios de BI',
                        )}
                      </Tag>
                    </Tooltip>
                  )}
                </Flex>
              </Box>

              <Box
                p="0.8"
                bg="#eee"
                ml="5"
                mr="5"
                display={{ base: 'none', lg: 'block' }}
                alignSelf="stretch"
              />

              <Flex align="baseline" minW="0">
                <Label>Descrição:</Label>
                <Text color={item.description ? undefined : 'gray.500'}>
                  {item.description || '—'}
                </Text>
              </Flex>
            </Flex>
          </Box>
        );
      })}
    </Box>
  );
}
